import { FlightDetailsInfoList } from "@flights/search-ui";
import React, { useRef } from "react";
import useFlightDetailsInfoController from "./useFlightDetailsInfo.controller";
import { UIFetchStatus, UIFlightData } from "@flights/types";
import { Spinner, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useIsInViewport from "../../../../../hooks/useIsInViewport";
import flights_web_condensed_extras_fd_mdot from "../../../../../utils/experiments/funnel/flights_web_condensed_extras_fd_mdot";
import useRouteName from "../../../../../hooks/useRouteName";
import flights_apex_web_show_included_baggage_asap from "../../../../../utils/experiments/apex/flights_apex_web_show_included_baggage_asap";
import { useTrackSplitBoundsBaggageAsap } from "../../../../../utils/experiments/funnel/flights_split_bounds_us";
import { useFlightCardLuggageController } from "../../../FlightCard/components/elements/FlightCardLuggage/useFlightCardLuggage.controller";
import flights_web_fd_baggage_transparency, {
  useTrackBaggageTransparencyFD
} from "../../../../../utils/experiments/funnel/flights_web_fd_baggage_transparency";
import useBaggageInfo from "./Items/BaggageInfo/useBaggageInfo";
import useDetailedBaggageInfo from "./Items/DetailedBaggageInfo/useDetailedBaggageInfo";

type Props = {
  flight: UIFlightData;
  fetchStatus: UIFetchStatus;
};

const FlightDetailsInfoMobile = ({ flight, fetchStatus }: Props) => {
  const { isLoading, items } = useFlightDetailsInfoController({ flight, fetchStatus });
  const i18n = useI18n();
  /* start - flights_web_condensed_extras_fd_mdot */
  const condensedExtrasTrackingRef = useRef<HTMLDivElement>(null);
  const isInViewport = useIsInViewport(condensedExtrasTrackingRef);
  const routeName = useRouteName();
  flights_web_condensed_extras_fd_mdot.useTrackStages(isInViewport && routeName === "flightdetails");
  /* end - flights_web_condensed_extras_fd_mdot */
  const baggageInfo = useBaggageInfo(flight);
  const detailedBaggageInfo = useDetailedBaggageInfo(flight);

  /* start - flights_apex_web_show_included_baggage_asap */
  const showBaggageInfoAsap =
    flights_apex_web_show_included_baggage_asap.isEligible(flight) &&
    !!flights_apex_web_show_included_baggage_asap.trackWithDefaultStage();
  const baggageInfoAsapItem = flights_web_fd_baggage_transparency.trackWithDefaultStage()
    ? detailedBaggageInfo
    : baggageInfo;

  const loaderRef = flights_apex_web_show_included_baggage_asap.useTrackStages({ isMobile: true, flight });
  /* end - flights_apex_web_show_included_baggage_asap */

  useTrackSplitBoundsBaggageAsap(showBaggageInfoAsap);

  // This is only tracking goals for `flights_web_sr_baggage_transparency` exp,
  // remove it when the exp is stopped
  useFlightCardLuggageController(flight, false, true);

  useTrackBaggageTransparencyFD(flight, !!showBaggageInfoAsap);

  const loadingComponent = (
    <Stack direction="row" attributes={{ ref: loaderRef }}>
      <Spinner />
      <Text variant="body_2">{i18n.trans(t("flights_fd_loading_more"))}</Text>
    </Stack>
  );

  if (showBaggageInfoAsap) {
    return (
      <>
        <FlightDetailsInfoList
          attributes={{ "data-testid": "flightdetails_ancillaries" }}
          variant="one-column"
          items={isLoading ? (!!baggageInfoAsapItem ? [baggageInfoAsapItem] : []) : items}
        />
        {isLoading ? loadingComponent : undefined}
        <div ref={condensedExtrasTrackingRef}></div>
      </>
    );
  }

  return (
    <>
      <FlightDetailsInfoList
        attributes={{ "data-testid": "flightdetails_ancillaries" }}
        variant="one-column"
        isLoading={isLoading}
        loadingComponent={loadingComponent}
        items={items}
      />
      <div ref={condensedExtrasTrackingRef}></div>
    </>
  );
};

export default FlightDetailsInfoMobile;
